import * as React from "react"
import { graphql } from "gatsby"
import { withPrismicPreview } from "gatsby-plugin-prismic-previews"
import { SliceZone } from "@prismicio/react"

import Layout from "../components/Layout"
import { components } from "../slices"
import Seo from "../components/Seo"
import {
  buildFaqPage,
  extractFaqItems,
  buildBreadcrumbs,
  SITE_URL,
} from "../utils/structuredData"
// import { useScrollRestoration } from "gatsby"

const PageTemplate = ({ data }) => {
  // const scrollRestoration = useScrollRestoration(`page-template-scroll`)
  if (!data) return null

  const pageContent = data.prismicPage
  const page = pageContent.data || {}
  // Use localized navbar/footer if available, otherwise fallback to English
  const footer = data.prismicFooter || data.prismicFooterFallback || {}
  const navbar = data.prismicNavbar || data.prismicNavbarFallback || {}

  const { lang, type, url } = pageContent
  const alternateLanguages = pageContent.alternate_languages || []
  const activeDoc = {
    lang,
    type,
    url,
    alternateLanguages,
  }

  return (
    <Layout navbar={navbar.data} activeDocMeta={activeDoc} footer={footer.data}>
      <SliceZone
        slices={page.body}
        components={components}
      // {...scrollRestoration}
      />
    </Layout>
  )
}

export const Head = ({ data, location }) => {
  const page = data.prismicPage || {}

  // Auto-generate FAQPage schema from any FAQ slice on this page so AI
  // engines can extract question/answer pairs directly.
  const faqPage = buildFaqPage(extractFaqItems(page.data?.body))

  // Breadcrumb: Home > this page (helps engines place the page in context).
  const title = page.data?.display_title || page.data?.meta_title
  const breadcrumbs =
    page.url && page.url !== "/"
      ? buildBreadcrumbs([
          { name: "Home", url: `${SITE_URL}/` },
          { name: title || page.uid, url: `${SITE_URL}${page.url}/` },
        ])
      : null

  const schema = [faqPage, breadcrumbs].filter(Boolean)

  return (
    <Seo
      title={data.prismicPage.data.meta_title}
      description={data.prismicPage.data.meta_description}
      keywords={data.prismicPage.data.meta_keywords}
      lang={page.lang}
      pathname={location.pathname}
      alternateLanguages={page.alternate_languages}
      schema={schema}
    />
  )
}

export const query = graphql`
  query pageQuery($id: String, $lang: String) {
    prismicPage(id: { eq: $id }, lang: { eq: $lang }) {
      _previewable
      url
      uid
      type
      tags
      id
      lang
      alternate_languages {
        id
        type
        lang
        uid
      }
      data {
        display_title
        meta_title
        meta_description
        meta_keywords {
          keyword
        }
        body {
          ... on PrismicSlice {
            id
            slice_label
            slice_type
          }
          ...PageDataBodyArticleWithImage
          ...PageDataBodyBackgroundVideo
          ...PageDataBodyBanner
          ...PageDataBodyCtaWithCardsDoubleLinks
          ...PageDataBodyCtaWithCardsImages
          ...PageDataBodyCtaWithFullWidthImage
          ...PageDataBodyCtaWithLeftImage
          ...PageDataBodyCtaWithRightImage
          ...PageDataBodyCtaWithRightImageLinks
          ...PageDataBodyDemoWithTabsImages
          ...PageDataBodyEmbeddedFormWithInfo
          ...PageDataBodyFaq
          ...PageDataBodyHero
          ...PageDataBodyHeroVideo
          ...PageDataBodyTwoColumnContent
          ...PageDataBodyFeaturesWithCardsImages
          ...PageDataBodyGridCardsWithImages
          ...PageDataBodyHeadline
          ...PageDataBodyInfoWithBackgroundImage
          ...PageDataBodyInfoWithFullWidthImage
          ...PageDataBodyInfoWithFullWidthVideo
          ...PageDataBodyInfoWithLeftImage
          ...PageDataBodyInfoWithRightImage
          ...PageDataBodyInfoWithRightImageItems
          ...PageDataBodyInfoWithRightImageSides
          ...PageDataBodyJoinUs
          ...PageDataBodyPartnersWithCardsImages
          ...PageDataBodyPosts
          ...PageDataBodyRedirectWithCardsImages
          ...PageDataBodyDownloadsWithCardsImages
          ...PageDataBodyStatistics
          ...PageDataBodyStepsWithCardsImages
          ...PageDataBodyTestimonials
          ...PageDataBodyTestSlice
          ...PageDataBodyWrittenByWithImage
          ...PageDataBodyPricing
        }
      }
    }
    prismicNavbar(lang: { eq: $lang }) {
      ...NavbarFragment
    }
    prismicNavbarFallback: prismicNavbar(lang: { eq: "en-gb" }) {
      ...NavbarFragment
    }
    prismicFooter(lang: { eq: $lang }) {
      ...FooterFragment
    }
    prismicFooterFallback: prismicFooter(lang: { eq: "en-gb" }) {
      ...FooterFragment
    }
  }
`

export default withPrismicPreview(PageTemplate)
